import React, { useEffect, useCallback } from 'react';
import ChatHeader from './ChatHeader';
import ContextBanner from './ContextBanner';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { useChat } from './ChatContext';
import { trans } from '../utils';

/**
 * Chat Panel - Main sliding container for the chat interface
 */
const ChatPanel = ({ isOpen, onClose }) => {
  const {
    messages,
    inputValue,
    setInputValue,
    isLoading,
    currentContext,
    attachedContext,
    sendMessage,
    attachContext,
    removeAttachedContext,
    startNewConversation
  } = useChat();

  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap - keep focus within panel when open
  useEffect(() => {
    if (isOpen) {
      // Focus the input when panel opens
      const input = document.querySelector('.chat-text-input');
      if (input) {
        setTimeout(() => input.focus(), 100);
      }
    }
  }, [isOpen]);

  // Handle suggestion click from empty state
  const handleSuggestionClick = useCallback((suggestion) => {
    setInputValue(suggestion);
  }, [setInputValue]);

  // Handle send message
  const handleSend = useCallback((content) => {
    sendMessage(content);
  }, [sendMessage]);

  // Context banner dismiss (optional - context stays but banner hides)
  const [bannerDismissed, setBannerDismissed] = React.useState(false);

  const handleDismissBanner = useCallback(() => {
    setBannerDismissed(true);
  }, []);

  // Reset banner when context changes
  useEffect(() => {
    setBannerDismissed(false);
  }, [currentContext?.description]);

  return (
    <>
      {/* Backdrop for mobile */}
      <div 
        className={`chat-panel-backdrop ${isOpen ? 'is-visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Main Panel */}
      <div 
        className={`chat-panel ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={trans('Chat Assistant')}
      >
        <ChatHeader 
          onClose={onClose}
          contextDescription={currentContext?.description}
          onNewChat={messages.length > 0 ? startNewConversation : null}
        />
        
        {currentContext && !bannerDismissed && messages.length === 0 && (
          <ContextBanner 
            context={currentContext}
            onDismiss={handleDismissBanner}
          />
        )}
        
        <ChatMessages 
          messages={messages}
          isLoading={isLoading}
          onSuggestionClick={handleSuggestionClick}
        />
        
        <ChatInput
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSend}
          onAttachContext={attachContext}
          attachedContext={attachedContext}
          onRemoveContext={removeAttachedContext}
          isLoading={isLoading}
          hasContext={!!currentContext}
        />
      </div>
    </>
  );
};

export default ChatPanel;
